/**
 * WebAuthn passkey wrappers using @simplewebauthn/browser. Backend pairs
 * with `re_platform_core.auth.passkey` (py-webauthn). user_verification is
 * REQUIRED on both registration + authentication per Section 5.2 of the
 * blueprint (biometric / PIN; not just touch).
 *
 * These wrappers only call `navigator.credentials.create/get` via SimpleWebAuthn —
 * they do NOT talk to our HTTP API. The host app (apps/web) is responsible for
 * POSTing the result to /identity/passkey/register/finish (or /login/finish).
 * Keeps this package free of api-client / fetch coupling.
 */

import {
  browserSupportsWebAuthn,
  startAuthentication,
  startRegistration,
} from "@simplewebauthn/browser";
import type {
  AuthenticationResponseJSON,
  PublicKeyCredentialCreationOptionsJSON,
  PublicKeyCredentialRequestOptionsJSON,
  RegistrationResponseJSON,
} from "@simplewebauthn/types";

export interface PasskeyRegistrationResult {
  credentialId: string;
  response: RegistrationResponseJSON;
}

export interface PasskeyAuthResult {
  credentialId: string;
  response: AuthenticationResponseJSON;
}

/** Re-export so callers don't have to depend on @simplewebauthn/browser directly. */
export const isPasskeySupported = browserSupportsWebAuthn;

export async function registerPasskey(
  options: PublicKeyCredentialCreationOptionsJSON,
): Promise<PasskeyRegistrationResult> {
  if (!browserSupportsWebAuthn()) {
    throw new Error("This browser does not support WebAuthn passkeys");
  }
  // SimpleWebAuthn handles base64url ↔ ArrayBuffer for us. user_verification
  // is REQUIRED per blueprint, but is enforced by the server's
  // py-webauthn verifier (require_user_verification=True), not the client —
  // attestation reflects whatever the authenticator did.
  const response = await startRegistration({ optionsJSON: options });
  return { credentialId: response.id, response };
}

export async function authenticatePasskey(
  options: PublicKeyCredentialRequestOptionsJSON,
): Promise<PasskeyAuthResult> {
  if (!browserSupportsWebAuthn()) {
    throw new Error("This browser does not support WebAuthn passkeys");
  }
  const response = await startAuthentication({ optionsJSON: options });
  return { credentialId: response.id, response };
}
